import * as React from 'react';
import { useCallback, useMemo } from 'react';

import { useMutation, useQuery } from '@apollo/client';
import { useGateValue } from '@coursera-web/statsig';
import { captureException } from '@sentry/react';

import { isAuthenticatedUser } from 'js/lib/user';

import { ORG_SCOPED_HOME_STATSIG_GATE } from 'bundles/unified-common/constants/orgScopedHomeStatsig';
import {
  type UpdateUserExperienceInput,
  UserExperienceContext,
  type UserExperienceSelection,
} from 'bundles/unified-common/contexts/UserExperienceContext';
import GetUserExperienceContextQuery from 'bundles/unified-common/queries/GetUserExperienceContextQuery.graphql';
import UpdateUserExperienceContextMutation from 'bundles/unified-common/queries/UpdateUserExperienceContextMutation.graphql';
import type { GetUserExperienceContextQuery as GetUserExperienceContextQueryType } from 'bundles/unified-common/queries/__generated__/GetUserExperienceContextQuery';
import type {
  UpdateUserExperienceContextMutation as UpdateUserExperienceContextMutationType,
  UpdateUserExperienceContextMutationVariables,
} from 'bundles/unified-common/queries/__generated__/UpdateUserExperienceContextMutation';

type UserExperienceContextProviderProps = {
  children: React.ReactNode;
};

function buildMutationInput(input: UpdateUserExperienceInput): UpdateUserExperienceContextMutationVariables['input'] {
  const { selection, organizationId, degreeProgramId } = input;
  if (selection === 'consumer') {
    return { selection: { consumer: {} } };
  }
  if (selection === 'degree' && degreeProgramId) {
    return { selection: { degree: { degreeProgramId } } };
  }
  if (selection === 'enterprise' && organizationId) {
    return { selection: { enterprise: { organizationId } } };
  }
  return { selection: { consumer: {} } };
}

export const UserExperienceContextProvider = ({ children }: UserExperienceContextProviderProps) => {
  // Use statsig gate directly (not useIsOrgScopedHome) to avoid circular dependency:
  // useIsOrgScopedHome needs product features → needs org ID → needs this query to resolve org.
  // Exposure is not logged here — it's logged by useIsOrgScopedHome once both conditions are confirmed.
  // @ts-expect-error FIXME: please avoid using the `disableExposureLog` and use qualifying events to filter exposures
  const userInOrgScopedRollout = useGateValue(ORG_SCOPED_HOME_STATSIG_GATE, { disableExposureLog: true });
  const skip = !userInOrgScopedRollout || !isAuthenticatedUser();

  const { data, loading, error } = useQuery<GetUserExperienceContextQueryType>(GetUserExperienceContextQuery, {
    context: { clientName: 'gatewayGql' },
    skip,
    errorPolicy: 'all',
    onError: (errorObj) => {
      captureException(errorObj, {
        extra: { message: 'Error fetching user experience context in UserExperienceContextProvider' },
      });
    },
  });

  const storedExperience: UserExperienceSelection = data?.UserExperienceContext?.getUserExperienceContext ?? null;

  const [updateMutation, { loading: isUpdating }] = useMutation<
    UpdateUserExperienceContextMutationType,
    UpdateUserExperienceContextMutationVariables
  >(UpdateUserExperienceContextMutation, {
    context: { clientName: 'gatewayGql' },
    refetchQueries: [{ query: GetUserExperienceContextQuery }],
  });

  const updateUserExperience = useCallback(
    async (input: UpdateUserExperienceInput): Promise<UserExperienceSelection> => {
      const result = await updateMutation({
        variables: { input: buildMutationInput(input) },
      });

      const payload = result.data?.UserExperienceContext_UpdateUserExperienceContext;
      if (payload?.__typename === 'UserExperienceContext_UpdateUserExperienceContextResultSuccess') {
        return payload.selection;
      }
      return null;
    },
    [updateMutation]
  );

  const value = useMemo(
    () => ({
      userExperience: storedExperience,
      isLoading: loading,
      error: error ?? null,
      updateUserExperience,
      isUpdating,
    }),
    [storedExperience, loading, error, updateUserExperience, isUpdating]
  );

  return <UserExperienceContext.Provider value={value}>{children}</UserExperienceContext.Provider>;
};

export default UserExperienceContextProvider;
